import { initializeLuckyTurnipsState } from '../LuckyTurnips/LuckyTurnipsReducer';
import { LevelStatus } from '../Level/LevelStatus';
import { PlayerStateHelper } from '../Player/PlayerStateHelper';
import { IEndLevelData, IGameLoadData, ILoadWorldMetaData, LevelFailReason } from './GameActions';
import Game from '../../Game';
import { PreBoosterType } from '../Card/BoosterType';
import { IAPReward } from 'Utility/RewardHelper';
import { RewardType } from 'Utility/Rewards/RewardType';
import UIStateHelper from '../UI/UIStateHelper';
import { AsyncActionStatus } from '../AsyncAction';
import { checkHelperFriendEnabled } from '../Level/HelperFriend/HelperFriendStateHelper';
import { FBTrackingEvent } from 'Utility/Tracking/FBTrackingEvent';
import { getTrackingDataSet, resetEventHandlerData, STRING_DATA_NOT_FOUND, track, TrackMethod } from 'Utility/Tracking/Track';
import { LevelHelperSettings, unlockThreeStar } from 'Constants/Level';
import { FTUXTrackingEventName, MissionTrackingEventName, TrackingEventName } from 'Utility/Tracking/TrackingEvent';
import { TActionType, TransactionType } from 'Utility/Tracking/DataSet/Transaction';
import { trackResourceEvent } from 'Utility/Tracking/TrackingHelpers/Transaction';
import { LevelType } from '../Player/Progress/PlayerLevels';
import { FTUXStateHelper } from './FTUX/FTUXStateHelper';
import { getLevelStarsTotal } from '../Player/PlayerLevelHelper';
import LevelStateHelper, {
    canShowInterstitialAd,
    getHardLevelBGKey,
    isDynamicAILevel,
} from '../Level/LevelStateHelper';
import { initialLevelState } from '../Level/initialLevelState';
import { GameScene } from './GameScene';
import { IGameState } from './IGameState';
import { initialUIState } from '../UI/IUIState';
import { report } from 'report';
import LevelHelper from '../../Utility/LevelHelper';
import { isNative, isShopSupported } from 'Utility/Platform';
import { MyPetMiniGameSceneParams } from 'Scene/MyPetMiniGameScene';
import { StarChestStateHelper } from '../Player/StarChests/StarChestStateHelper';
import { ProgressStateHelper } from '../Player/Progress/ProgressStateHelper';
import { PopupID } from '../UI/PopupID';
import { LoginStateHelper } from '../Player/Login/LoginStateHelper';
import { PetsStateHelper } from '../Player/Pets/PetsStateHelper';
import PayloadStateHelper from './Payload/PayloadStateHelper';
import { IAPStateHelper, IPurchaseConsumptionData } from '../Player/IAP/IAPStateHelper';
import { ConditionalTrackingHelper } from 'Utility/Tracking/TrackingHelpers/ConditionalTracking';
import { FBPixel } from 'Utility/SDK/FBPixelHandler';
import { scheduleInactivityNotification } from 'State/Game/Action/ScheduleNotification';
import { ISceneParams } from 'Scene/Scene';
import { FarmStateHelper } from 'State/Player/Farm/FarmStateHelper';
import { FTUXType } from 'Player/PlayerProperty/Progress/FTUXType';
import { QuestStateHelper } from 'State/Player/Quests/QuestStateHelper';
import { Native } from '@sgorg/mobile-dev-tools';
import CardStateHelper from 'State/Card/CardStateHelper';
import { CardModifierType } from 'Prototype/Level/Card/CardModifierType';
import { resetPlinkoScene } from 'State/Plinko/PlinkoStateHelper';
import { IPlayerStateTaskData } from 'State/Player/IPlayerState';
import { PlayerTask } from 'State/Player/PlayerTasks';
import { ContextType } from './ContextType';
import { AdsConfig } from 'Constants/Ads';
import { AdvertisementArea } from 'Utility/SDK/Advertisement';
import ContextStateHelper from './Context/ContextStateHelper';
import { WorldDataHelper } from 'Utility/WorldDataHelper';
import SDKIntegrationsHelper from 'Utility/SDK/SDKIntegrationsHelper';
import { initialState } from './initialState';
import { GBCHandler } from 'Utility/SDK/GBCHandler';
import { ILevelDataFile } from 'System/Level/Data/ILevelDataFile';
import { AvoTracking } from 'Utility/Tracking/Avo/AvoTracking';
import CheatsManager from 'CheatsManager';

export interface ILoadLevelMetaData {
    levelNumber: number;
    levelCost?: number;
    preBoosters: PreBoosterType[];
    restart?: boolean;
    attempts?: number;
    levelType?: LevelType;
    isFreePlay?: boolean;
    isLevelTest?: boolean;
    debugLevelData?: ILevelDataFile;
}

export default class GameStateHelper {
    public static loadWorld(state: IGameState, params?: ILoadWorldMetaData): IGameState {
        const highestLevelCompleted = Game.player.getHighestLevelCompleted(state, LevelType.Core);

        // Unlock next world if not already unlocked
        if (ProgressStateHelper.canUnlockNextWorld(state, highestLevelCompleted)) {
            state = {
                ...state,
                playerState: ProgressStateHelper.unlockNextWorld(state.playerState),
            };
        }

        // Set star chest level if needed
        // FIXME: loading a world shouldn't be a requirement to spawn a star chest.
        //        move this logic to where user reaches new level
        const levelToSpawnStarChestOn = StarChestStateHelper.getLevelToSpawnStarChestOn(state);
        if (levelToSpawnStarChestOn !== null) {
            state = {
                ...state,
                playerState: StarChestStateHelper.setStarChestLevel(state.playerState, levelToSpawnStarChestOn),
            };
        }

        params = params || {};
        const world = params.worldNumber ?? state.playerState.maxWorld;
        params.clearUI = params.clearUI ?? true;
        params.firstComplete = Boolean(params.firstComplete);
        params.firstLoad = Boolean(params.firstLoad);

        let UIState = params.clearUI ? initialUIState : state.UIState;

        if (state.UIState.queue.length > 0) {
            UIState = UIStateHelper.nextPopup(state.UIState);
        }

        // Reset level state before checking for FTUX.
        state = {
            ...state,
            levelState: initialLevelState,
        };

        state = PlayerStateHelper.queueFTUXIfNeeded(state);

        state = QuestStateHelper.fillQuestsIfNeeded(state);

        state = GameStateHelper.queueScene(state, GameScene.WORLD);

        return {
            ...state,
            worldState: {
                worldNumber: world,
                firstComplete: params.firstComplete,
                firstLoad: params.firstLoad,
                starsGained: params.starsGained,
                petSpawnLevel: params.petSpawnLevel,
            },
            UIState: UIState,
        };
    }

    public static queueScene(state: IGameState, scene: GameScene, params?: any): IGameState {
        return {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: scene,
                params,
            },
        };
    }

    public static loadLevel(state: IGameState, params: ILoadLevelMetaData): IGameState {
        //Defaults
        params.attempts = params.attempts !== undefined ? params.attempts : 1;
        params.levelType = params.levelType !== undefined ? params.levelType : LevelType.Core;

        // Get level data
        let levelData = ProgressStateHelper.getLevelData(state, params.levelNumber);

        // If no level data then create a new entry
        if (!levelData) {
            levelData = {
                levelNumber: params.levelNumber,
                stars: null,
                helperFriendAttempts: 0,
                consecutiveUnsuccessfullAttempts: 0,
                attempts: 0,
            };
        }

        state = GameStateHelper.queueScene(state, GameScene.LEVEL);

        // Set state
        state = {
            ...state,
            levelState: {
                ...initialLevelState,
                levelNumber: params.levelNumber,
                levelCost: params.levelCost,
                levelType: params.levelType,
                attempts: params.attempts,
                score: 0,
                status: LevelStatus.LOADING,
                // Reset booster prices at start of level
                boosterUses: undefined,
                boosterPrices: undefined,
                isFreePlay: params.isFreePlay,
                isLevelTest: params.isLevelTest ?? false,
                preBoosters: params.preBoosters,
                debugLevelData: params.debugLevelData,
            },
            UIState: initialUIState, // Reset UI state.
        };

        state = LevelStateHelper.getBoosterPrices(state);

        // Update the number of attempts since the level has been completed and since the helper was used
        state = ProgressStateHelper.updateLevel(state, params.levelNumber, {
            consecutiveUnsuccessfullAttempts: (levelData.consecutiveUnsuccessfullAttempts || 0) + 1,
            helperFriendAttempts: (levelData.helperFriendAttempts || 0) + 1,
            attempts: (levelData.attempts || 0) + 1,
        });

        if (isDynamicAILevel(state)) {
            levelData = ProgressStateHelper.getLevelData(state, params.levelNumber);

            if (levelData.consecutiveUnsuccessfullAttempts > LevelHelperSettings.minAttemptsToReduceDifficulty) {
                const difficultyReduction = state.playerState.progress.difficultyReduction + 1;
                state = PlayerStateHelper.setDifficultyReduction(state, difficultyReduction);
            }
        }

        // Check if friend helper should be activated this level
        state = checkHelperFriendEnabled(state);

        // Tracking
        track('mLevel', params.levelNumber);
        track('missionID', `level_saga_${params.levelNumber}`);
        track('missionName', `Level ${params.levelNumber}`);
        track('mLevelType', 'ALL_CARDS');
        track('mRetryNum', params.attempts - 1);
        track('lastLevelAttempted', params.levelNumber);
        track('tCoinsLevelEntry', params.levelCost);
        track('tActionType', TActionType.LEVEL_ENTRY);
        track('mEntryCoinCost', params.levelCost);
        track('mEntryWithBooster', params.isFreePlay ? 1 : 0);

        // Subtract coins in the levelSystem after the level has loaded, to ensure the players only spend coins on levels they get to play

        return state;
    }

    public static loadLuckyTurnips(state: IGameState): IGameState {
        state = GameStateHelper.queueScene(state, GameScene.LUCKY_TURNIPS);
        return {
            ...state,
            playerState: {
                ...state.playerState,
                luckyTurnipsState: initializeLuckyTurnipsState(
                    StarChestStateHelper.getStarChestUnlockTier(state.playerState),
                    state.playerState.luckyTurnipsState
                ),
            },
        };
    }

    public static loadLevelOrShowNoCoinsPopup(state: IGameState, params: ILoadLevelMetaData): IGameState {
        const { levelNumber, restart = false } = params;
        const starCount = ProgressStateHelper.getLevelStars(state, levelNumber);
        let levelCost: number = LevelHelper.getLevelEntryCost(levelNumber, starCount > 0);

        const isFreePlay: boolean = levelCost > 0 && state.playerState.inventory[RewardType.FREE_PLAY] > 0;

        if (isFreePlay) {
            levelCost = 0;
        }

        if (state.playerState.inventory.coins >= levelCost) {
            return GameStateHelper.loadLevel(state, {
                levelNumber,
                levelCost,
                isFreePlay,
                isLevelTest: params.isLevelTest,
                preBoosters: params.preBoosters,
                ...(restart ? { attempts: state.levelState.attempts + 1 } : {}),
            });
        } else {
            if (restart) {
                //:TODO SOMETHING IS BROKEN HERE...
                state = GameStateHelper.loadWorld(state, { worldNumber: state.worldState.worldNumber });
            }

            return {
                ...state,
                UIState: UIStateHelper.showPopup(
                    state.UIState,
                    isShopSupported(state) ? PopupID.SHOP_MENU : PopupID.NO_COINS_MENU,
                    {}
                ),
            };
        }
    }

    public static loadMyPetMiniGame(state: IGameState, params: MyPetMiniGameSceneParams): IGameState {
        return GameStateHelper.queueScene(state, GameScene.MY_PET_MINI_GAME, params);
    }

    public static loadFarmScene(state: IGameState, params: ISceneParams): IGameState {
        // Validate farm scene is not already loaded or currently loading.
        if (state.activeScene.result === GameScene.FARM) return state;

        state = GameStateHelper.queueScene(state, GameScene.FARM, params);

        state = FarmStateHelper.queueFarmFTUXIfNeeded(state);

        if (!FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM_BTN)) {
            state = {
                ...state,
                playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXType.FARM_BTN]),
            };
        }

        // Reset preview slot states to prevent showing confirm-animations when loading the scene.
        state = FarmStateHelper.clearFarmSlotPreviews(state);

        return state;
    }

    public static loadPlinkoScene(state: IGameState, params: ISceneParams): IGameState {
        // Validate plinko scene is not already loaded or currently loading.
        if (state.activeScene.result === GameScene.PLINKO) return state;

        state = {
            ...state,
            activeScene: {
                status: AsyncActionStatus.QUEUED,
                result: GameScene.PLINKO,
                params,
            },
        };

        state = resetPlinkoScene(state, {
            purchasableTokensRemaining: params.purchasableTokensRemaining,
        });

        // TODO: Plinko queue FTUX
        // state = FarmStateHelper.queueFarmFTUXIfNeeded(state);

        // if (!FTUXStateHelper.isFtuxCompleted(state.playerState, FTUXType.FARM_BTN)) {
        //     state = {
        //         ...state,
        //         playerState: FTUXStateHelper.updateFTUXProgress(state.playerState, [FTUXType.FARM_BTN]),
        //     };
        // }

        return state;
    }

    public static endLevel(state: IGameState, payload: IEndLevelData) {
        const interstitialData = {
            placement: AdsConfig.getInterstitialDefault(Game.config),
            payload: {
                g_gameArea: AdvertisementArea.LEVEL_COMPLETE,
            },
        };
        // Check before sending the resource again, its already sent on level fail popup is shown
        GameStateHelper.trackLevelEnd(state, payload);
        state = FTUXStateHelper.setLevelTasks(state);

        const status: LevelStatus =
            payload.status === LevelStatus.COMPLETE || payload.status === LevelStatus.FAILED
                ? LevelStatus.EXITING
                : payload.status;

        // If leaving the scene then queue required tasks
        if (status === LevelStatus.EXITING) {
            const playerTasks: IPlayerStateTaskData[] = [];
            // queue interstitialAd if needed
            if (canShowInterstitialAd(state)) {
                playerTasks.push({
                    task: PlayerTask.CONTEXT_TASK,
                    prePlayTask: true,
                    contextType: ContextType.DISPLAY_ADVERTISEMENT,
                    data: interstitialData,
                });
            }

            // Queue three star share if needed
            if (
                state.levelState.firstAttempt &&
                state.levelState.completeData?.stars === 3 &&
                state.levelState.levelNumber >= unlockThreeStar
            ) {
                // playerTasks.push({
                //     task: PlayerTask.SHOW_POPUP,
                //     prePlayTask: true,
                //     popup: PopupID.THREE_STAR_SHARE_MENU,
                //     meta: {
                //         levelNumber: state.levelState.levelNumber,
                //         firstComplete: state.levelState.firstAttempt,
                //     },
                //     delay: 300,
                // });
            }

            const hardLevelAsset = getHardLevelBGKey(state.levelState.levelNumber);
            if (
                state.levelState.completeData &&
                WorldDataHelper.isHardLevel(state.levelState.levelNumber) &&
                hardLevelAsset &&
                Game.resource.existsAndLoaded(hardLevelAsset)
            ) {
                // playerTasks.push({
                //     task: PlayerTask.SHOW_POPUP,
                //     prePlayTask: true,
                //     popup: PopupID.HARD_LEVEL_SHARE_POPUP,
                //     meta: {
                //         levelNumber: state.levelState.levelNumber,
                //         firstComplete: state.levelState.completeData?.firstComplete,
                //     },
                //     delay: 300,
                // });
            }

            // Add tasks to queue
            if (playerTasks.length > 0) {
                state = {
                    ...state,
                    playerState: PlayerStateHelper.addStateTaskDatas(state.playerState, playerTasks),
                };
            }
            // Else if staying in the level scene and can show an ad, then play the ad
        } else if (payload.status === LevelStatus.RESTARTING && canShowInterstitialAd(state)) {
            state = UIStateHelper.hidePopupFullState(state, PopupID.LEVEL_POPUP, {});
            state = LevelStateHelper.setLevelPreBoosters(state, payload.preBoosters);

            state = ContextStateHelper.setContext(state, ContextType.DISPLAY_ADVERTISEMENT, interstitialData);
        }

        return {
            ...state,
            levelState: {
                ...state.levelState,
                status: status,
                firstComplete: payload.firstComplete,
            },
        };
    }

    public static trackLevelEnd(state: IGameState, payload: IEndLevelData): void {
        try {
            const stars: number = getLevelStarsTotal(state.playerState);
            const levelComplete = payload.status === LevelStatus.COMPLETE;
            const failedByTNT = payload.failReason === LevelFailReason.TNT_EXPLODED;
            const isTNTActive = CardStateHelper.hasActiveModifierType(state, CardModifierType.tnt);

            track('stars', stars);
            track('mChestStarsGained', state.levelState.completeData?.starsGained ?? 0);
            track('mStars', state.levelState.completeData?.stars ?? 0);
            track('mScore', state.levelState.completeData?.score ?? 0);
            track('mCoinsReceived', getTrackingDataSet().tCoinsReceived);
            track('tCoinsLevelEnd', state.levelState.completeData?.coins ?? 0);
            track('mMovesMade', state.levelState.cardsState.turns);
            track('lastLevelWon', Game.player.getHighestLevelCompleted(state));
            track('mCardsLeftOnBoard', state.levelState.cardsState.inPlay.length);
            track('mTNTActive', Number(failedByTNT));
            track('tTNTActive', Number(isTNTActive));
            track('tCoinsReceived', state.levelState.coinsCollected, TrackMethod.ADD);


            trackResourceEvent(state, {
                transactionType: TransactionType.LEVEL_END,
                tActionType: TActionType.LEVEL_END,
            });
            AvoTracking.levelFinished(state, levelComplete ? 'Success' : 'Failed');
            resetEventHandlerData(
                levelComplete ? MissionTrackingEventName.missionCompleted : MissionTrackingEventName.missionFailed,
                state
            );
        } catch (error) {
            // preventing error 2541750930 on Sentry
            // because if `cardsState` is undefined then usually we already sent it
            const logFunc = Boolean(state.levelState?.cardsState) ? report : console.log;
            logFunc('Failed to track:', { error, once: true, sampleRate: 0.1 });
        }
        track('tCoinsLevelEnd', 0);
        track('tCoinsReceived', 0);
    }

    public static gameLoaded(state: IGameState, params: IGameLoadData): IGameState {
        state = {
            ...state,
            userState: {
                ...state.userState,
                ...params.userState,
            },
            platform: {
                ...state.platform,
                ...params.platform,
                adsInterstitial: params.platform.adsInterstitial,
                adsRewarded: params.platform.adsRewarded,
                subscribeBot: params.platform.subscribeBot,
            },
            entryPoint: params.entryPoint,
            worldState: {
                ...state.worldState,
                worldNumber: params.playerState.maxWorld,
            },
            eventsState: params.eventsState,
        };

        // Initial setting of player state.
        state.playerState = PlayerStateHelper.updatePlayerState(state, params.playerState);

        // Call incrementSessionCount before checkDailyLogin to ensure that the daily session count is properly reset if its a new day
        state = LoginStateHelper.incrementSessionCount(state);
        state = LoginStateHelper.setFirstLoadTimer(state);
        state = LoginStateHelper.checkDailyLogin(state);
        state = PlayerStateHelper.checkTimers(state);

        state = ConditionalTrackingHelper.trackConditionally(state, [
            FBTrackingEvent.SESSION_5_3DAYS,
            FBTrackingEvent.SESSION_8_5DAYS,
            FBTrackingEvent.SESSION_11_7DAYS,
            FBTrackingEvent.SESSION_2_1DAY,
            FBTrackingEvent.SESSION_2_2DAYS,
            FBTrackingEvent.SESSION_2_3DAYS,
            FBTrackingEvent.SESSION_3_1DAY,
            FBTrackingEvent.SESSION_3_2DAYS,
            FBTrackingEvent.SESSION_3_3DAYS,
            FBTrackingEvent.SESSION_4_1DAY,
            FBTrackingEvent.SESSION_4_2DAYS,
            FBTrackingEvent.SESSION_4_3DAYS,
            FBTrackingEvent.SESSION_5_1DAY,
            FBTrackingEvent.SESSION_5_2DAYS,
            FBTrackingEvent.SESSION_5_5DAYS,
            FBTrackingEvent.SESSION_5_7DAYS,
        ]);

        state = PayloadStateHelper.handleEntryPoint(state, params.entryPoint);

        state = PlayerStateHelper.setIsNewUser(state);

        if (state.perSessionData.isNewUser) {
            state = PetsStateHelper.setIsPetInDaily(state, true);
            FBPixel.track('Pixel_SOFA_NewUser');
        }

        if (state.perSessionData.isNewUser) {
            resetEventHandlerData(TrackingEventName.newPlayer, state);
            state = PlayerStateHelper.setPlayerState(state, { ...state.playerState });
        }

        track('gbFailBeforeSuccess', Number(GBCHandler.getFailedBeforeSuccess()));
        resetEventHandlerData(TrackingEventName.gameStarted, state);
        AvoTracking.gameInitialized(state);

        // Track that cheats were enabled.
        if (CheatsManager.getFirstInstance()?.cheatsEnabled) 
            // AvoTracking.cheatsEnabled();

        if (isNative()) {
            scheduleInactivityNotification(state);
            state = PlayerStateHelper.setDeviceId(state, Native.main.getDeviceId());
        }

        // Only track GBC-init event after player data is loaded - otherwise we don't know if we've already seen this event or not.
        // NOTE: The GBC is initialized in `Game.ts` and must always we loaded before the player data is loaded - therefore we do not
        // check if the GBC is initialized. If the GBC was initialized asynchronously then a GBC-init flag should be added to the state
        // and this tracking event should be validated/sent with a new GBC-init action OR this action - whenever they're both complete.
        state = GameStateHelper.trackGBCInitFTUE(state);

        // Track game-loaded event between GBC-init and gameplay-shown.
        state = GameStateHelper.trackGameLoadedFTUE(state);

        // Track gameplay-shown event after player data is loaded. Previously this event was sent in `SDKManager.loadComplete()` however the player data
        // was not loaded in time and could not be validated if this event had already been sent before. Because the SDK must be loaded by this point
        // in the game lifecycle we can send this event as soon as player data is loaded (here).
        state = GameStateHelper.trackGameplayShownFTUE(state);

        return state;
    }

    public static enableIAP(state: IGameState, params: IPurchaseConsumptionData[]): IGameState {
        if (params) {
            state = IAPStateHelper.handleUnconsumedPurchases(state, params);
        }

        return {
            ...state,
            platform: {
                ...state.platform,
                IAPSupport: true,
            },
        };
    }

    public static trackGBCInitFTUE(state: IGameState) {
        track(
            'userID',
            state.userState.userId ||
                state.playerState.facebookUserId ||
                state.playerState.deviceId ||
                STRING_DATA_NOT_FOUND
        );

        if (state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_01_GbcInit)) {
            return state;
        }

        track('stepId', 1);
        track('stepName', FBTrackingEvent.FTUE_01_GbcInit);
        resetEventHandlerData(FTUXTrackingEventName.ftux, state);
        return PlayerStateHelper.addFBTrackingEvent(state, FBTrackingEvent.FTUE_01_GbcInit);
    }

    public static trackGameLoadedFTUE(state: IGameState) {
        if (state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_02_LoadingCompleted)) {
            return state;
        }

        SDKIntegrationsHelper.gameTracking(FBTrackingEvent.FTUE_02_LoadingCompleted);

        track('stepId', 2);
        track('stepName', FBTrackingEvent.FTUE_02_LoadingCompleted);
        resetEventHandlerData(FTUXTrackingEventName.ftux, state);

        return PlayerStateHelper.addFBTrackingEvent(state, FBTrackingEvent.FTUE_02_LoadingCompleted);
    }

    public static trackGameplayShownFTUE(state: IGameState) {
        if (state.playerState.FBEvents.includes(FBTrackingEvent.FTUE_03_GameplayShown)) {
            return state;
        }

        SDKIntegrationsHelper.gameTracking(FBTrackingEvent.FTUE_03_GameplayShown);

        track('stepId', 3);
        track('stepName', FBTrackingEvent.FTUE_03_GameplayShown);
        resetEventHandlerData(FTUXTrackingEventName.ftux, state);

        return PlayerStateHelper.addFBTrackingEvent(state, FBTrackingEvent.FTUE_03_GameplayShown);
    }

    public static reconnectedToGBC(state: IGameState): IGameState {
        state = {
            ...state,
            gamePaused: false,
            gbcStatus: AsyncActionStatus.COMPLETE,
        };

        state = UIStateHelper.hidePopupFullState(state, PopupID.GBC_DISCONNECTED);

        return state;
    }

    public static disconnectedFromGBC(state: IGameState): IGameState {
        state = {
            ...state,
            gamePaused: true,
            gbcStatus: AsyncActionStatus.EMPTY,
            dataConflict: initialState.dataConflict,
        };
        state = UIStateHelper.requeueCurrentPopup(state, undefined, [
            PopupID.GBC_DISCONNECTED,
            PopupID.DATA_CONFLICT_POPUP,
        ]);
        state = UIStateHelper.showPopupFullState(state, PopupID.GBC_DISCONNECTED, {});
        return state;
    }

    public static isGBCConnected(state: IGameState): boolean {
        return state.gbcStatus === AsyncActionStatus.COMPLETE;
    }

    public static isGBCConnectionFullyResolved(state: IGameState): boolean {
        return (
            state.gbcStatus === AsyncActionStatus.COMPLETE && state.dataConflict.status === AsyncActionStatus.COMPLETE
        );
    }

    public static setGamePaused(state: IGameState, gamePaused: boolean): IGameState {
        return {
            ...state,
            gamePaused,
        };
    }
}
